import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
import { baseurImage } from "../../Baseurl";
export default function ViewInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load invoice
  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(`getInvoiceById/${id}`);
        if (res.data?.success && res.data?.data) {
          setInvoice(res.data.data);
        } else if (state?.invoice) {
          setInvoice(state.invoice);
        }
      } catch (_) {
        if (state?.invoice) setInvoice(state.invoice);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [id]);

  const fmt  = (n, d = 3) => Number(n || 0).toFixed(d);
  const fmtDateTime = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    return `${d.toLocaleDateString("en-GB")} ${d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}`;
  };

  const statusColor = (s) => {
    const v = (s || "").toLowerCase();
    if (v === "fully paid" || v === "paid")    return { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" };
    if (v === "not paid"   || v === "unpaid")  return { bg: "#fee2e2", color: "#991b1b", border: "#fca5a5" };
    return { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };
  };

  // Computed totals
  const services  = invoice?.services  || [];
  const pharmacy  = invoice?.pharmacy  || [];
  const labs      = invoice?.labs      || [];
  const payments  = invoice?.payments  || [];

  const totalServiceVat  = services.reduce((s, x) => s + Number(x.vat_value  || 0), 0);
  const totalPharmacyVat = pharmacy.reduce((s, x) => s + Number(x.vat_value  || 0), 0);
  const totalVat         = totalServiceVat + totalPharmacyVat;
  const totalDiscount    = services.reduce((s, x) => s + Number(x.discount_value || 0), 0);
  const grandTotal       = Number(invoice?.total_amount   || 0);
  const paidAmount       = Number(invoice?.paid_amount    || 0);
  const remaining        = Number(invoice?.remaining_amount ?? (grandTotal - paidAmount));
  const sc               = statusColor(invoice?.invoice_status || invoice?.status);

  const handlePrint = () => {
    const hs       = (() => { try { return JSON.parse(localStorage.getItem("businessSetting")) || {}; } catch { return {}; } })();
    const logoRaw  = localStorage.getItem("businessSettingImage") || "";
    const logoUrl  = logoRaw
      ? (logoRaw.startsWith("data:") || logoRaw.startsWith("http") ? logoRaw : `${baseurImage}${logoRaw}`)
      : (hs.image ? `${baseurImage}${hs.image}` : "");

    const hospitalName  = hs.official_name || hs.name || "Medical Center";
    const hospitalCR    = hs.cr_number   || "";
    const hospitalVAT   = hs.vat_number  || "";
    const hospitalTel   = hs.telephone_1 || hs.phone || "";
    const hospitalEmail = hs.email       || "";
    const hospitalAddr  = hs.address     || "";

    const serviceRows = services.map((s, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${s.serviceCode || "-"}</td>
        <td>${s.serviceName || s.name || "-"}</td>
        <td class="c">${s.quantity ?? 1}</td>
        <td class="r">${fmt(s.price)}</td>
        <td class="r">${fmt(s.discount_value)}</td>
        <td class="r">${s.vat_percent ?? 0}%</td>
        <td class="r">${fmt(s.vat_value)}</td>
        <td class="r"><b>${fmt(s.net_amount)}</b></td>
      </tr>`).join("");

    const pharmacyRows = pharmacy.map((p, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${p.serviceName || p.name || "-"}</td>
        <td class="c">${p.quantity ?? 1}</td>
        <td class="r">${fmt(p.price)}</td>
        <td class="r">${fmt(p.vat_value)}</td>
        <td class="r"><b>${fmt(p.net_amount)}</b></td>
      </tr>`).join("");

    const labRows = labs.map((l, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${l.serviceName || l.name || "-"}</td>
        <td>${l.category || "-"}</td>
        <td class="c">${l.quantity ?? 1}</td>
        <td class="r"><b>${fmt(l.net_amount)}</b></td>
      </tr>`).join("");

    const paymentRows = payments.map((p, i) => `
      <tr>
        <td class="c">${i + 1}</td>
        <td>${fmtDateTime(p.payment_date)}</td>
        <td>${p.payment_method || "-"}</td>
        <td>${p.transactionId || "-"}</td>
        <td class="r">${fmt(p.payment_amount)}</td>
      </tr>`).join("");

    const totalPayments = payments.reduce((s, p) => s + Number(p.payment_amount || 0), 0);

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>VAT Invoice #${invoice.invoice_no || invoice.id}</title>
  <style>
    @page { size: A4; margin: 20mm 22mm 16mm 22mm; }
    * { box-sizing: border-box; margin: 45; padding: 0; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 11pt; color: #000; background: #fff; print-color-adjust: exact; -webkit-print-color-adjust: exact; }

    /* ── Clinic header ── */
    .hdr { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 8px; border-bottom: 2px solid #000; margin-bottom: 8px; }
    .hdr-logo img  { max-height: 60px; max-width: 130px; object-fit: contain; display: block; }
    .hdr-logo .hname { font-size: 16pt; font-weight: bold; }
    .hdr-right { text-align: right; font-size: 10pt; line-height: 1.55; }
    .hdr-right .clinic-name { font-size: 13pt; font-weight: bold; margin-bottom: 2px; }

    /* ── Invoice meta bar ── */
    .meta-bar { display: flex; border: 1px solid #000; margin-bottom: 8px; }
    .meta-bar .title-cell { background: #fff; color: #000; display: flex; align-items: center; justify-content: center; padding: 6px 14px; white-space: nowrap; border-right: 1px solid #000; }
    .meta-bar .title-cell span { font-size: 13pt; font-weight: bold; letter-spacing: 1px; }
    .meta-bar .cell { flex: 1; padding: 4px 8px; border-left: 1px solid #000; }
    .meta-bar .lbl { font-size: 9pt; text-transform: uppercase; color: #555; letter-spacing: 0.3px; }
    .meta-bar .val { font-size: 11pt; font-weight: bold; margin-top: 1px; }

    /* ── Bill to ── */
    .bill-to { border: 1px solid #000; padding: 5px 9px; margin-bottom: 8px; display: flex; gap: 24px; flex-wrap: wrap; }
    .bill-to .field .lbl { font-size: 9pt; text-transform: uppercase; color: #555; }
    .bill-to .field .val { font-size: 11.5pt; font-weight: bold; margin-top: 1px; }

    /* ── Section title ── */
    .sec { font-size: 10pt; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 1.5px solid #000; padding-bottom: 2px; margin: 9px 0 3px; }

    /* ── Tables ── */
    table { width: 100%; border-collapse: collapse; font-size: 10.5pt; }
    th { border: 1px solid #000; padding: 3px 5px; text-align: left; font-size: 10pt; background: #ebebeb; font-weight: bold; }
    td { border: 1px solid #ccc; padding: 3px 5px; vertical-align: top; }
    tbody tr:nth-child(even) td { background: #f7f7f7; }
    tfoot td { border: 1px solid #000; font-weight: bold; background: #ebebeb; }
    .r { text-align: right; }
    .c { text-align: center; }

    /* ── Totals ── */
    .totals-wrap { display: flex; justify-content: flex-end; margin-top: 8px; }
    .totals-tbl { width: 250px; border-collapse: collapse; font-size: 11pt; }
    .totals-tbl td { padding: 3px 7px; border-bottom: 1px solid #ddd; }
    .totals-tbl .grand td { border-top: 2px solid #000; border-bottom: 2px solid #000; font-weight: bold; font-size: 12pt; background: #ebebeb; }
    .totals-tbl .sub td { background: #f5f5f5; font-weight: bold; }

    /* ── Footer ── */
    .footer { margin-top: 14px; border-top: 1px solid #999; padding-top: 5px; display: flex; justify-content: space-between; font-size: 9pt; color: #444; }
  </style>
</head>
<body>

  <!-- Clinic header -->
  <div class="hdr">
    <div class="hdr-logo">
      ${logoUrl
        ? `<img src="${logoUrl}" alt="logo"/>`
        : `<div class="hname">${hospitalName}</div>`}
    </div>
    <div class="hdr-right">
      <div class="clinic-name">${hospitalName}</div>
      ${hospitalAddr  ? `<div>${hospitalAddr}</div>` : ""}
      ${hospitalTel   ? `<div>Tel: ${hospitalTel}</div>` : ""}
      ${hospitalEmail ? `<div>${hospitalEmail}</div>` : ""}
      ${hospitalCR    ? `<div>CR: ${hospitalCR}</div>` : ""}
      ${hospitalVAT   ? `<div>VAT No: ${hospitalVAT}</div>` : ""}
    </div>
  </div>

  <!-- Invoice meta bar -->
  <div class="meta-bar">
    <div class="title-cell"><span>VAT INVOICE</span></div>
    <div class="cell">
      <div class="lbl">Invoice No</div>
      <div class="val">#${invoice.invoice_no || invoice.id}</div>
    </div>
    <div class="cell">
      <div class="lbl">Date</div>
      <div class="val">${fmtDateTime(invoice.invoice_date || invoice.updated_at)}</div>
    </div>
    <div class="cell">
      <div class="lbl">Status</div>
      <div class="val">${invoice.invoice_status || invoice.status || "-"}</div>
    </div>
  </div>

  <!-- Bill To -->
  <div class="bill-to">
    <div class="field">
      <div class="lbl">Bill To</div>
      <div class="val">${invoice.fullName || invoice.patient_name || "-"}</div>
    </div>
    ${(invoice.mobileNumber || invoice.MobileNumber) ? `<div class="field"><div class="lbl">Phone</div><div class="val">${invoice.mobileNumber || invoice.MobileNumber}</div></div>` : ""}
    ${invoice.civil_id ? `<div class="field"><div class="lbl">Civil ID</div><div class="val">${invoice.civil_id}</div></div>` : ""}
  </div>

  ${services.length > 0 ? `
  <div class="sec">Services</div>
  <table>
    <thead><tr><th class="c">#</th><th>Code</th><th>Description</th><th class="c">Qty</th><th class="r">Price</th><th class="r">Discount</th><th class="r">VAT%</th><th class="r">VAT Amt</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${serviceRows}</tbody>
  </table>` : ""}

  ${pharmacy.length > 0 ? `
  <div class="sec">Pharmacy</div>
  <table>
    <thead><tr><th class="c">#</th><th>Item</th><th class="c">Qty</th><th class="r">Unit Price</th><th class="r">VAT Amt</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${pharmacyRows}</tbody>
  </table>` : ""}

  ${labs.length > 0 ? `
  <div class="sec">Lab Tests</div>
  <table>
    <thead><tr><th class="c">#</th><th>Test Name</th><th>Category</th><th class="c">Qty</th><th class="r">Net (BHD)</th></tr></thead>
    <tbody>${labRows}</tbody>
  </table>` : ""}

  <!-- Totals -->
  <div class="totals-wrap">
    <table class="totals-tbl">
      ${totalDiscount > 0 ? `<tr><td>Total Discount</td><td class="r">${fmt(totalDiscount)} BHD</td></tr>` : ""}
      <tr><td>Total VAT</td><td class="r">${fmt(totalVat)} BHD</td></tr>
      <tr class="grand"><td>Grand Total</td><td class="r">${fmt(grandTotal)} BHD</td></tr>
      <tr class="sub"><td>Amount Paid</td><td class="r">${fmt(paidAmount)} BHD</td></tr>
      <tr class="sub"><td>Balance Due</td><td class="r">${fmt(remaining)} BHD</td></tr>
    </table>
  </div>

  ${payments.length > 0 ? `
  <div class="sec">Payment History</div>
  <table>
    <thead><tr><th class="c">#</th><th>Date &amp; Time</th><th>Method</th><th>Transaction ID</th><th class="r">Amount (BHD)</th></tr></thead>
    <tbody>${paymentRows}</tbody>
    <tfoot><tr><td colspan="4" class="r">Total Paid</td><td class="r">${fmt(totalPayments)} BHD</td></tr></tfoot>
  </table>` : ""}

  <div class="footer">
    <span>Computer-generated document — no signature required.</span>
    <span>Printed: ${new Date().toLocaleDateString("en-GB")} ${new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })}</span>
  </div>

  <script>window.onload = () => { setTimeout(() => { window.print(); window.onafterprint = () => window.close(); }, 350); }</script>
</body>
</html>`;

    const w = window.open("", "_blank");
    if (w) { w.document.open(); w.document.write(html); w.document.close(); }
  };

  if (loading) {
    return (
      <div className="pc-container"><div className="pc-content">
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div>
        </div>
      </div></div>
    );
  }

  if (!invoice) {
    return (
      <div className="pc-container"><div className="pc-content">
        <div className="text-center text-muted py-5">Invoice not found.</div>
      </div></div>
    );
  }

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">

            {/* ── Top action bar ── */}
            <div className="tableHeader d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="d-flex align-items-center gap-2">
                <span
                  style={{ cursor: "pointer", color: "#3f75c7", fontSize: "1.4rem", fontWeight: "bold" }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >&#8592;</span>
                <h5 className="mb-0">Invoice Details</h5>
              </div>
              <button className="bgBtn btn-sm d-flex align-items-center gap-1" onClick={handlePrint}>
                <i className="ti ti-printer f-16" /> Print
              </button>
            </div>

            <div className="card-body p-4">

              {/* ── Invoice header ── */}
              <div className="d-flex flex-wrap justify-content-between align-items-start mb-4 gap-3"
                style={{ borderBottom: "2px solid #e5e7eb", paddingBottom: "1.25rem" }}>

                {/* Invoice meta */}
                <div>
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="fw-bold mb-0" style={{ color: "#1e3a5f" }}>
                      #{invoice.invoice_no || invoice.id}
                    </h4>
                    <span style={{
                      padding: "4px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                      background: sc.bg, color: sc.color, border: `1px solid ${sc.border}`
                    }}>
                      {invoice.invoice_status || invoice.status || "-"}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>
                    <div><strong>Date:</strong> {fmtDateTime(invoice.invoice_date || invoice.updated_at)}</div>
                    {invoice.sent_by && <div><strong>Created by:</strong> {invoice.sent_by}</div>}
                  </div>
                </div>
              </div>

              {/* ── Patient info ── */}
              <div className="row mb-4">
                <div className="col-12">
                  <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 16px", border: "1px solid #e2e8f0" }}>
                    <div className="row g-2">
                      <div className="col-sm-4">
                        <span style={{ fontSize: 12, color: "#9ca3af", display: "block" }}>PATIENT NAME</span>
                        <strong>{invoice.fullName || invoice.patient_name || "-"}</strong>
                      </div>
                      <div className="col-sm-4">
                        <span style={{ fontSize: 12, color: "#9ca3af", display: "block" }}>PHONE</span>
                        <strong>{invoice.mobileNumber || invoice.MobileNumber || "-"}</strong>
                      </div>
                      <div className="col-sm-4">
                        <span style={{ fontSize: 12, color: "#9ca3af", display: "block" }}>TOTAL SERVICES</span>
                        <strong>{invoice.total_services || (services.length + pharmacy.length + labs.length)}</strong>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Services ── */}
              {services.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-semibold mb-2" style={{ color: "#374151" }}>
                    <i className="ti ti-stethoscope me-1" />Services
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead style={{ background: "#f1f5f9" }}>
                        <tr>
                          <th>#</th>
                          <th>Code</th>
                          <th>Name</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Price</th>
                          <th className="text-end">Discount</th>
                          <th className="text-end">VAT%</th>
                          <th className="text-end">VAT Amt</th>
                          <th className="text-end">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {services.map((s, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{s.serviceCode || "-"}</td>
                            <td>{s.serviceName || s.name || "-"}</td>
                            <td className="text-end">{s.quantity ?? 1}</td>
                            <td className="text-end">{fmt(s.price)} BHD</td>
                            <td className="text-end">{fmt(s.discount_value)} BHD</td>
                            <td className="text-end">{s.vat_percent ?? 0}%</td>
                            <td className="text-end">{fmt(s.vat_value)} BHD</td>
                            <td className="text-end">{fmt(s.net_amount)} BHD</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Pharmacy ── */}
              {pharmacy.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-semibold mb-2" style={{ color: "#374151" }}>
                    <i className="ti ti-pill me-1" />Pharmacy
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead style={{ background: "#f1f5f9" }}>
                        <tr>
                          <th>#</th>
                          <th>Item</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Unit Price</th>
                          <th className="text-end">VAT Amt</th>
                          <th className="text-end">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pharmacy.map((p, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{p.serviceName || p.name || "-"}</td>
                            <td className="text-end">{p.quantity ?? 1}</td>
                            <td className="text-end">{fmt(p.price)} BHD</td>
                            <td className="text-end">{fmt(p.vat_value)} BHD</td>
                            <td className="text-end">{fmt(p.net_amount)} BHD</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Labs ── */}
              {labs.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-semibold mb-2" style={{ color: "#374151" }}>
                    <i className="ti ti-flask me-1" />Lab Tests
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-bordered table-sm mb-0">
                      <thead style={{ background: "#f1f5f9" }}>
                        <tr>
                          <th>#</th>
                          <th>Test Name</th>
                          <th>Category</th>
                          <th className="text-end">Qty</th>
                          <th className="text-end">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labs.map((l, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{l.serviceName || l.name || "-"}</td>
                            <td>{l.category || "-"}</td>
                            <td className="text-end">{l.quantity ?? 1}</td>
                            <td className="text-end">{fmt(l.net_amount)} BHD</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* ── Financial summary ── */}
              <div className="row justify-content-end mb-4">
                <div className="col-md-5">
                  <table className="table table-sm mb-0" style={{ fontSize: 14 }}>
                    <tbody>
                      {totalDiscount > 0 && (
                        <tr>
                          <td className="text-muted">Total Discount</td>
                          <td className="text-end">{fmt(totalDiscount)} BHD</td>
                        </tr>
                      )}
                      <tr>
                        <td className="text-muted">Total VAT</td>
                        <td className="text-end">{fmt(totalVat)} BHD</td>
                      </tr>
                      <tr style={{ borderTop: "2px solid #e5e7eb" }}>
                        <td className="fw-bold">Grand Total</td>
                        <td className="text-end fw-bold">{fmt(grandTotal)} BHD</td>
                      </tr>
                      <tr>
                        <td style={{ color: "#059669" }}><strong>Paid</strong></td>
                        <td className="text-end" style={{ color: "#059669" }}><strong>{fmt(paidAmount)} BHD</strong></td>
                      </tr>
                      <tr>
                        <td style={{ color: remaining > 0 ? "#dc2626" : "#059669" }}><strong>Remaining</strong></td>
                        <td className="text-end" style={{ color: remaining > 0 ? "#dc2626" : "#059669" }}>
                          <strong>{fmt(remaining)} BHD</strong>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ── Payments breakdown ── */}
              <div className="mb-2">
                <h6 className="fw-semibold mb-2" style={{ color: "#374151" }}>
                  <i className="ti ti-cash me-1" />Payment Breakdown
                </h6>
                <div className="table-responsive">
                  <table className="table table-bordered table-sm mb-0">
                    <thead style={{ background: "#f1f5f9" }}>
                      <tr>
                        <th>#</th>
                        <th>Date</th>
                        <th>Method</th>
                        <th>Transaction ID</th>
                        <th>Received By</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.length === 0 ? (
                        <tr><td colSpan="6" className="text-center text-muted">No payments recorded</td></tr>
                      ) : (
                        payments.map((p, i) => (
                          <tr key={i}>
                            <td>{i + 1}</td>
                            <td>{fmtDateTime(p.payment_date)}</td>
                            <td>{p.payment_method || "-"}</td>
                            <td>{p.transactionId || "-"}</td>
                            <td>{p.sent_by || "-"}</td>
                            <td className="text-end">{fmt(p.payment_amount)} BHD</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {payments.length > 0 && (
                      <tfoot style={{ background: "#f1f5f9" }}>
                        <tr>
                          <td colSpan="5" className="text-end fw-bold">Total Paid</td>
                          <td className="text-end fw-bold">{fmt(payments.reduce((s, p) => s + Number(p.payment_amount || 0), 0))} BHD</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          .pc-sidebar, .pc-header, .tableHeader button, .pc-header { display: none !important; }
          .card { box-shadow: none !important; border: none !important; }
          .card-body { padding: 0 !important; }
        }
      `}</style>
    </div>
  );
}
